import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Código como TEXTO — script desofuscado, linha de comando, conteúdo hostil.
 *
 * O que entra aqui veio de uma máquina possivelmente comprometida: um script
 * PowerShell que o AMSI desofuscou, por exemplo. Por isso cada linha é um nó de
 * texto do React — escapado — e nada aqui usa `dangerouslySetInnerHTML`, realce
 * de sintaxe ou qualquer coisa que interprete o conteúdo. Um `<script>` dentro do
 * script aparece como os caracteres `<script>`, que é o que o analista precisa ler.
 *
 * Numeração por linha (para citar "linha 14" num ticket), quebra alternável
 * (linhas de 2 KB de base64 são o caso comum) e cópia do conteúdo exato.
 */
export function CodeBlock({
  code,
  label,
  maxHeight = '24rem',
}: {
  code: string
  /** Nome do conteúdo, para o botão de copiar e leitores de tela. */
  label: string
  maxHeight?: string
}) {
  const [wrap, setWrap] = useState(false)
  const [copied, setCopied] = useState<'idle' | 'ok' | 'fail'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  const copy = useCallback(async () => {
    try {
      // Copia o conteúdo EXATO, não as linhas renderizadas: nenhum número de
      // linha nem quebra visual vai junto.
      await navigator.clipboard.writeText(code)
      setCopied('ok')
    } catch {
      setCopied('fail')
    }
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied('idle'), 2_000)
  }, [code])

  // `\r\n` de Windows vira uma quebra só; um `\r` solto também é quebra.
  const lines = code.split(/\r\n|\r|\n/)
  const gutter = String(lines.length).length

  return (
    <div className="border border-line-subtle bg-surface">
      <div className="flex items-center gap-2 border-b border-line-subtle px-2 py-1">
        <span className="text-micro uppercase tracking-label text-fg-muted">
          {`${lines.length} ${lines.length === 1 ? 'linha' : 'linhas'} · ${code.length} caracteres`}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            aria-pressed={wrap}
            onClick={() => setWrap((v) => !v)}
            className="border border-line-strong px-1 py-px text-micro uppercase tracking-label text-fg-muted hover:bg-inset hover:text-fg"
          >
            {wrap ? 'sem quebra' : 'quebrar linhas'}
          </button>
          <button
            type="button"
            onClick={() => void copy()}
            title={`Copiar ${label}`}
            aria-label={`Copiar ${label}`}
            className="border border-line-strong px-1 py-px text-micro uppercase tracking-label text-fg-muted hover:bg-inset hover:text-fg"
          >
            {copied === 'ok' ? 'copiado' : copied === 'fail' ? 'falhou' : 'copiar'}
          </button>
        </div>
      </div>
      <div className="overflow-auto" style={{ maxHeight }}>
        <pre
          aria-label={label}
          className="min-w-0 py-1 font-mono text-micro leading-snug text-fg-secondary"
        >
          <code className={wrap ? 'block' : 'inline-block min-w-full'}>
            {lines.map((line, i) => (
              <span key={i} className="flex">
                <span
                  aria-hidden
                  className="sticky left-0 shrink-0 select-none border-r border-line-subtle bg-surface pr-2 pl-2 text-right text-fg-muted"
                  style={{ width: `${gutter + 2}ch` }}
                >
                  {i + 1}
                </span>
                <span
                  className={`min-w-0 pl-2 pr-2 ${wrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre'}`}
                >
                  {/* Linha vazia ainda ocupa altura — senão a numeração desalinha. */}
                  {line === '' ? ' ' : line}
                </span>
              </span>
            ))}
          </code>
        </pre>
      </div>
    </div>
  )
}
